import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { createPost } from "../../utils/apiClient";
import {
  buildGroundingBody,
  defaultGroundingTitle,
  getGroundingMusicSettings,
  loadGroundingPrompts,
  pickRandomPrompts,
} from "../../utils/groundingPrompts";
import PromptText from "./PromptText";

function GroundingJourneyDialog({ open, onClose, onCreated }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const audioRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [musicSrc, setMusicSrc] = useState("/grounding-music.flac");
  const [musicStartSeconds, setMusicStartSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [musicProgress, setMusicProgress] = useState(0);

  const stepCount = steps.length;
  const currentStep = steps[step];
  const currentAnswer = answers[step] || "";
  const isLastStep = stepCount > 0 && step === stepCount - 1;
  const showInput = currentStep ? !currentStep.autoskip : false;
  const canProceed = showInput && Boolean(currentAnswer.trim());

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const seekAudioToStart = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || musicStartSeconds <= 0) return;

    const seek = () => {
      const max = Number.isFinite(audio.duration) ? audio.duration : musicStartSeconds;
      audio.currentTime = Math.min(musicStartSeconds, Math.max(0, max - 0.25));
    };

    if (audio.readyState >= 1) {
      seek();
      return;
    }

    audio.addEventListener("loadedmetadata", seek, { once: true });
  }, [musicStartSeconds]);

  const startAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    seekAudioToStart();
    try {
      await audio.play();
      seekAudioToStart();
    } catch {
      audio.muted = true;
      setMuted(true);
      try {
        await audio.play();
        seekAudioToStart();
      } catch {
        /* browser blocked autoplay */
      }
    }
  }, [seekAudioToStart]);

  useEffect(() => {
    if (!open) {
      stopAudio();
      return undefined;
    }

    let cancelled = false;

    setStep(0);
    setSteps([]);
    setAnswers([]);
    setError("");
    setSaving(false);
    setMusicProgress(0);
    setLoading(true);

    (async () => {
      try {
        const config = await loadGroundingPrompts();
        if (cancelled) return;
        const picked = pickRandomPrompts(config);
        const music = getGroundingMusicSettings(config);
        setSteps(picked);
        setAnswers(Array(picked.length).fill(""));
        setMusicSrc(music.src);
        setMusicStartSeconds(music.startSeconds);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Kunne ikke indlæse prompts.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, stopAudio]);

  useEffect(() => {
    if (!open || loading || !steps.length) return undefined;
    startAudio();
    return () => stopAudio();
  }, [open, loading, steps.length, musicSrc, musicStartSeconds, startAudio, stopAudio]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = muted;
    }
  }, [muted]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !open) return undefined;

    const updateProgress = () => {
      if (!Number.isFinite(audio.duration) || audio.duration <= 0) {
        setMusicProgress(0);
        return;
      }
      setMusicProgress(Math.min(1, audio.currentTime / audio.duration));
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", updateProgress);
    audio.addEventListener("seeked", updateProgress);
    updateProgress();

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", updateProgress);
      audio.removeEventListener("seeked", updateProgress);
    };
  }, [open, musicSrc, loading, steps.length]);

  const goToNextStep = useCallback(() => {
    setStep((prev) => prev + 1);
    setError("");
  }, []);

  useEffect(() => {
    if (!open || loading || !currentStep?.autoskip || isLastStep) return undefined;

    const timeout = window.setTimeout(goToNextStep, currentStep.duration * 1000);

    return () => window.clearTimeout(timeout);
  }, [open, loading, step, currentStep, isLastStep, goToNextStep]);

  const handleNext = async () => {
    if (!canProceed) return;

    setError("");

    if (!isLastStep) {
      goToNextStep();
      return;
    }

    setSaving(true);
    try {
      const body = buildGroundingBody(steps, answers);
      await createPost({
        title: defaultGroundingTitle(),
        body,
        composition: "grounding",
        tags: "grounding",
        visibility: "private",
      });
      stopAudio();
      onCreated?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    setError("");
    setStep((prev) => Math.max(0, prev - 1));
  };

  const handleClose = () => {
    stopAudio();
    onClose();
  };

  const footer = (
    <Box sx={{ flexShrink: 0, position: "relative" }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: "#e8e8e8",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${musicProgress * 100}%`,
            bgcolor: "#111111",
            transition: "width 0.2s linear",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          pt: 1.25,
          minHeight: 52,
          pb: "max(8px, env(safe-area-inset-bottom))",
        }}
      >
        <audio ref={audioRef} src={musicSrc} loop preload="auto" />
        <IconButton
          aria-label={muted ? "Slå lyd til" : "Slå lyd fra"}
          onClick={() => setMuted((value) => !value)}
          disabled={loading || saving}
          size="small"
          sx={{ ml: -0.5 }}
        >
          {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button onClick={handleClose} disabled={saving || loading} size="small">
            Annuller
          </Button>
          {step > 0 ? (
            <Button onClick={handleBack} disabled={saving || loading} size="small">
              Tilbage
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={saving || loading || !steps.length || !canProceed}
            size="small"
          >
            {saving ? "Gemmer..." : isLastStep ? "Gem" : "Næste"}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      scroll={fullScreen ? "paper" : "paper"}
      PaperProps={{
        sx: fullScreen
          ? {
              display: "flex",
              flexDirection: "column",
              height: "100%",
              maxHeight: "100dvh",
              overflow: "hidden",
              overscrollBehavior: "none",
            }
          : undefined,
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          pr: 6,
          py: 1.5,
          ...(fullScreen && { borderBottom: "1px solid", borderColor: "divider" }),
        }}
      >
        Ny refleksion
        <IconButton
          aria-label="Luk"
          onClick={handleClose}
          disabled={saving}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <Box
        sx={{
          flex: fullScreen ? 1 : undefined,
          minHeight: fullScreen ? 0 : undefined,
          overflow: fullScreen ? "hidden" : undefined,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fcfcfc",
          ...(fullScreen
            ? {}
            : {
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
              }),
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error && !steps.length ? (
          <Typography color="error" align="center" sx={{ p: 3 }}>
            {error}
          </Typography>
        ) : currentStep ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: fullScreen ? 1 : undefined,
              minHeight: fullScreen ? 0 : 280,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: showInput ? "0 0 auto" : "1 1 auto",
                display: "flex",
                alignItems: showInput ? "flex-start" : "center",
                justifyContent: "center",
                px: 2,
                py: showInput ? (fullScreen ? 1.5 : 2) : 3,
                ...(showInput && {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }),
              }}
            >
              <PromptText text={currentStep.text} atTop={showInput} />
            </Box>

            {showInput ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  p: 2,
                  pt: 1.5,
                }}
              >
                <TextField
                  value={currentAnswer}
                  onChange={(e) => {
                    const next = [...answers];
                    next[step] = e.target.value;
                    setAnswers(next);
                    if (error) setError("");
                  }}
                  placeholder="Skriv dit svar her..."
                  multiline
                  minRows={fullScreen ? 3 : 6}
                  fullWidth
                  autoFocus
                  disabled={saving}
                  inputProps={{ style: { fontSize: 16 } }}
                  sx={{
                    flex: fullScreen ? 1 : undefined,
                    minHeight: fullScreen ? 0 : undefined,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#ffffff",
                      borderRadius: 2,
                      ...(fullScreen && {
                        height: "100%",
                        alignItems: "flex-start",
                      }),
                    },
                    ...(fullScreen && {
                      "& .MuiInputBase-inputMultiline": {
                        height: "100% !important",
                        overflow: "auto !important",
                        boxSizing: "border-box",
                      },
                    }),
                  }}
                />
              </Box>
            ) : null}

            {error ? (
              <Typography color="error" variant="body2" align="center" sx={{ flexShrink: 0, px: 2, pb: 1 }}>
                {error}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {footer}
    </Dialog>
  );
}

export default GroundingJourneyDialog;
